import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1649079800856-4b9c19d64ab4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJhbCUyMGludGVyaW9yJTIwbXV0ZWQlMjBncmV5JTIwc3RvbmV8ZW58MHx8fHwxNzkyOTcwOTY5fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["Sound as credible", "as you are."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-[#26282E]">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Muted minimalist architectural interior in grey stone with soft natural light"
          className="h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#26282E]/75 via-[#26282E]/55 to-[#26282E]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#26282E]/80 via-transparent to-[#26282E]/40" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-44 sm:px-8 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E3D9C8]"
          data-testid="hero-overline"
        >
          Executive Voice &amp; Communication Coaching — Austin, TX · Virtual Worldwide
        </motion.p>

        <h1 className="mt-8 font-serif text-5xl leading-[1.04] tracking-tight text-[#F7F5F1] sm:text-6xl lg:text-[5.25rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? (
                  <>
                    as <em className="text-[#8FB0D4]">you are.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#C4BFB4] sm:text-lg"
          data-testid="hero-subheadline"
        >
          Executive voice and communication coaching for leaders whose words carry weight — executives, attorneys,
          senior leaders, and founders preparing to raise capital. Precise vocal mechanics, in service of one
          outcome: a voice that earns trust in high-stakes rooms.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-consultation"
            className="group inline-flex items-center justify-center gap-3 rounded-md bg-[#2F5D8C] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:bg-[#254A6E]"
          >
            Request a Confidential Consultation
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="#who-i-work-with"
            data-testid="hero-cta-founders"
            className="inline-flex items-center justify-center gap-3 rounded-md border border-[#F7F5F1]/25 px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:border-[#E3D9C8]/70 hover:text-[#E3D9C8]"
          >
            For Founders Raising Capital
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-3 border-t border-[#F7F5F1]/10 pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A8D94]"
        >
          <span>Clarity</span>
          <span>Credibility</span>
          <span>Composure</span>
          <span>Vocal Stamina</span>
        </motion.div>
      </div>
    </section>
  );
}
